import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Formik } from 'formik-2';
import { withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import { GET_AB_TEST, GET_AB_TESTING_METADATA } from '../../queries';
import {
    DEFAULT_END_DATE,
    DEFAULT_START_DATE,
    EMPTY_TEST_SKELETON,
    getSchemaBySteps,
    TEST_WIZARD_STEPS,
} from './consts';
import css from './WizardWrapper.css';
import { Spinner } from '../../../components/widgets';
import WizardContainer from './WizardContainer';
import { normalizePost, normalizeTestChannels } from './Steps/wizardUtils';
import { SAVE_AB_TEST } from '../../mutations';
import { trackMixpanelEvent } from '../../../utils/general';
import { MIX_PANEL_KEYS, TRACK_EVENT_PREFIX } from '../../consts';
import { AUDIENCE_ABTESTING_STATUS } from '../tableUtils';
import SubPageHeader from '../../../components/molecules/SubPageHeader';

const normalizeGetAbTest = values => {
    return {
        id: values?.id,
        testName: values?.name || '',
        description: values?.description || '',
        appsite: {
            label: `${values.appsite.appName} - ${values.appsite.platform}`,
            value: values.appsite.id,
            icon: <img src={values.appsite.icon} alt="" className={css.icon} />,
            className: css.dropdownRow,
        },
        segment: {
            value: values.segment.id,
            label: values.segment.name,
            reach: values.segment.reach,
        },
        startDate: values?.startDate || DEFAULT_START_DATE,
        endDate: values?.endDate || DEFAULT_END_DATE,
        isTestOngoing: !values?.endDate,
        draftStep: values?.draftStep || TEST_WIZARD_STEPS[0],
        events: values?.events || [],
        testChannels: normalizeTestChannels(values?.testChannels || []),
        status: values.status,
        internalDistribution: values?.internalDistribution,
    };
};

const prefixTranslate = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD';

const WizardWrapper = ({ onClose, refetchTable, testId }) => {
    const { data: abTestDataResponseData, loading: isFetchingTest, refetch: refetchAbTest } = useQuery(GET_AB_TEST, {
        skip: !testId,
        fetchPolicy: 'cache-first',
        variables: { testId },
    });
    const { data: existingTestsData } = useQuery(GET_AB_TESTING_METADATA, {
        fetchPolicy: 'cache-first',
    });
    const [activeStepIndex, setActiveStepIndex] = useState(0);
    const [saveAbTest, saveAbTestResponse] = useMutation(SAVE_AB_TEST, {
        onCompleted: () => {
            if (testId) {
                refetchAbTest({ testId });
            }
            refetchTable();
            saveAbTestResponse.reset();
            onClose();
        },
    });

    const initialValues = useMemo(
        () => (isFetchingTest || !testId ? EMPTY_TEST_SKELETON : normalizeGetAbTest(abTestDataResponseData.abTest)),
        [isFetchingTest, abTestDataResponseData, testId]
    );

    const onSubmit = async values => {
        const mixPanelKey = values.isDraft ? MIX_PANEL_KEYS.WIZARD_DRAFT_SAVE : MIX_PANEL_KEYS.WIZARD_TEST_SCHEDULED;
        trackMixpanelEvent(TRACK_EVENT_PREFIX, mixPanelKey);
        await saveAbTest({ variables: { abTest: normalizePost(values) } });
    };

    useEffect(() => {
        if (!isFetchingTest && testId) {
            if (initialValues.draftStep && initialValues.status === AUDIENCE_ABTESTING_STATUS.EDIT) {
                const stepIndex = TEST_WIZARD_STEPS.indexOf(initialValues.draftStep);
                if (stepIndex !== -1) {
                    setActiveStepIndex(stepIndex);
                }
            } else {
                setActiveStepIndex(TEST_WIZARD_STEPS.length - 1);
            }
        }
    }, [isFetchingTest, testId, initialValues.draftStep]);

    const validationSchema = useMemo(() => {
        const currentStepKey = TEST_WIZARD_STEPS[activeStepIndex];
        const existingTestNames = existingTestsData?.abTestingMetadata || [];
        const schemaBySteps = getSchemaBySteps(existingTestNames, testId);
        return schemaBySteps[currentStepKey] || null;
    }, [activeStepIndex, existingTestsData, testId]);

    const submitData = useMemo(
        () => ({
            loading: !!saveAbTestResponse.loading,
            error: !!saveAbTestResponse.error,
        }),
        [saveAbTestResponse]
    );

    return (
        <div className={css.pageContainer}>
            <SubPageHeader
                onGoBack={onClose}
                labelTranslationKey={initialValues?.id ? `${prefixTranslate}.HEADER_EDIT` : `${prefixTranslate}.HEADER`}
                labelTextReplace={{ testName: initialValues?.testName || '' }}
            />
            {isFetchingTest ? (
                <Spinner show expanded />
            ) : (
                <Formik
                    validateOnBlur={false}
                    validationSchema={validationSchema}
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                >
                    <WizardContainer
                        changeWizardStep={setActiveStepIndex}
                        activeStepIndex={activeStepIndex}
                        submitData={submitData}
                        onClose={onClose}
                    />
                </Formik>
            )}
        </div>
    );
};

WizardWrapper.propTypes = {
    onClose: PropTypes.func.isRequired,
    refetchTable: PropTypes.func.isRequired,
    testId: PropTypes.string,
};

export default withLocalize(WizardWrapper);
